import { baseApi } from './baseApi';

const toQuery = (filter = {}) => {
  const p = new URLSearchParams();
  Object.entries(filter).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') p.append(k, v);
  });
  const s = p.toString();
  return s ? `?${s}` : '';
};

export const callLogsApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    listCallLogs: build.query({
      query: (filter) => `/call-logs${toQuery(filter)}`,
      transformResponse: (resp) => resp.data,
      providesTags: (result) => [
        { type: 'CallLog', id: 'LIST' },
        ...(result?.items?.map((c) => ({ type: 'CallLog', id: c.id })) ?? []),
      ],
    }),
    getCallLog: build.query({
      query: (id) => `/call-logs/${id}`,
      transformResponse: (resp) => resp.data,
      providesTags: (_r, _e, id) => [{ type: 'CallLog', id }],
    }),
    getCallLogStats: build.query({
      query: () => '/call-logs/stats',
      transformResponse: (resp) => resp.data,
      providesTags: [{ type: 'CallLog', id: 'STATS' }],
    }),
    callLogAnalytics: build.query({
      query: ({ from, to } = {}) => `/call-logs/analytics${toQuery({ from, to })}`,
      transformResponse: (resp) => resp.data,
      providesTags: [{ type: 'CallLog', id: 'ANALYTICS' }],
    }),
    recordCall: build.mutation({
      query: (body) => ({ url: '/call-logs/record', method: 'POST', body }),
      transformResponse: (resp) => resp.data,
      invalidatesTags: [{ type: 'CallLog', id: 'LIST' }, { type: 'CallLog', id: 'STATS' }],
    }),
    markCallReviewed: build.mutation({
      query: (id) => ({ url: `/call-logs/${id}/reviewed`, method: 'PATCH' }),
      transformResponse: (resp) => resp.data,
      invalidatesTags: (_r, _e, id) => [
        { type: 'CallLog', id },
        { type: 'CallLog', id: 'LIST' },
        { type: 'CallLog', id: 'STATS' },
      ],
    }),
    convertCallToLead: build.mutation({
      query: ({ id, ...body }) => ({ url: `/call-logs/${id}/convert-to-lead`, method: 'POST', body }),
      transformResponse: (resp) => resp.data,
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'CallLog', id },
        { type: 'CallLog', id: 'LIST' },
        { type: 'CallLog', id: 'STATS' },
        { type: 'Lead', id: 'LIST' },
        'Dashboard',
      ],
    }),
    discardCall: build.mutation({
      query: ({ id, reason }) => ({ url: `/call-logs/${id}/discard`, method: 'PATCH', body: { reason } }),
      transformResponse: (resp) => resp.data,
      // Optimistic update — flip the row to discarded immediately so the UI doesn't lag the
      // round-trip. Rolled back on error. Each cached list page is patched separately.
      async onQueryStarted({ id, reason }, { dispatch, queryFulfilled, getState }) {
        const patches = [];
        for (const { endpointName, originalArgs } of callLogsApi.util.selectInvalidatedBy(getState(), [{ type: 'CallLog', id: 'LIST' }])) {
          if (endpointName !== 'listCallLogs') continue;
          patches.push(dispatch(callLogsApi.util.updateQueryData('listCallLogs', originalArgs, (draft) => {
            const row = draft?.items?.find((x) => x.id === id);
            if (row) {
              row.isDiscarded = true;
              row.discardedAt = new Date().toISOString();
              row.status = 'Discarded';
            }
          })));
        }
        try { await queryFulfilled; }
        catch { patches.forEach((p) => p.undo()); }
      },
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'CallLog', id },
        { type: 'CallLog', id: 'LIST' },
        { type: 'CallLog', id: 'STATS' },
      ],
    }),
    // Send-to-other: routes a call away from the lead funnel. RoutedTo is required;
    // reason is free-text context.
    routeCall: build.mutation({
      query: ({ id, routedTo, reason }) => ({
        url: `/call-logs/${id}/route`,
        method: 'PATCH',
        body: { routedTo, reason: reason ?? null },
      }),
      transformResponse: (resp) => resp.data,
      async onQueryStarted({ id, routedTo, reason }, { dispatch, queryFulfilled, getState }) {
        const patches = [];
        for (const { endpointName, originalArgs } of callLogsApi.util.selectInvalidatedBy(getState(), [{ type: 'CallLog', id: 'LIST' }])) {
          if (endpointName !== 'listCallLogs') continue;
          patches.push(dispatch(callLogsApi.util.updateQueryData('listCallLogs', originalArgs, (draft) => {
            const row = draft?.items?.find((x) => x.id === id);
            if (row) {
              row.isRoutedToOther = true;
              row.routedTo = routedTo;
              row.routedReason = reason ?? null;
              row.routedAt = new Date().toISOString();
              row.status = 'SentToOther';
            }
          })));
        }
        try { await queryFulfilled; }
        catch { patches.forEach((p) => p.undo()); }
      },
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'CallLog', id },
        { type: 'CallLog', id: 'LIST' },
        { type: 'CallLog', id: 'STATS' },
      ],
    }),
    // Single-shot "mark all New as Reviewed" — fired by the sidebar on nav-click so the
    // unseen counter zeros out server-side, not just in localStorage.
    markAllCallsSeen: build.mutation({
      query: () => ({ url: '/call-logs/mark-all-seen', method: 'PATCH' }),
      transformResponse: (resp) => resp.data,
      invalidatesTags: [{ type: 'CallLog', id: 'LIST' }, { type: 'CallLog', id: 'STATS' }],
    }),
    deleteCall: build.mutation({
      query: (id) => ({ url: `/call-logs/${id}`, method: 'DELETE' }),
      invalidatesTags: (_r, _e, id) => [
        { type: 'CallLog', id },
        { type: 'CallLog', id: 'LIST' },
        { type: 'CallLog', id: 'STATS' },
      ],
    }),
  }),
});

export const {
  useListCallLogsQuery, useLazyListCallLogsQuery,
  useGetCallLogQuery,
  useGetCallLogStatsQuery,
  useCallLogAnalyticsQuery,
  useRecordCallMutation,
  useMarkCallReviewedMutation,
  useConvertCallToLeadMutation,
  useDiscardCallMutation,
  useRouteCallMutation,
  useMarkAllCallsSeenMutation,
  useDeleteCallMutation,
} = callLogsApi;
